import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { ThumbsUp, Plus, Trash2, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IdeaBoxPage() {
  const { user, api } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [submitting, setSubmitting] = useState(false);

  const fetchIdeas = () => {
    api.get(`/ideas?sort=${sortBy}`)
      .then((r) => setIdeas(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchIdeas(); }, [sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/ideas", { title, description });
      toast.success("Idea posted!");
      setTitle(""); setDescription(""); setShowNew(false);
      fetchIdeas();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post idea");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (ideaId) => {
    try {
      const r = await api.post(`/ideas/${ideaId}/upvote`);
      setIdeas(ideas.map(i => i.idea_id === ideaId ? r.data : i));
    } catch { /* ignore */ }
  };

  const handleDelete = async (ideaId) => {
    try {
      await api.delete(`/ideas/${ideaId}`);
      setIdeas(ideas.filter(i => i.idea_id !== ideaId));
      toast.success("Idea removed");
    } catch { /* ignore */ }
  };

  return (
    <main className="min-h-screen bg-slate-50 pt-24 md:pt-28 pb-24" data-testid="idea-box-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="flex items-start justify-between mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-2">Community</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">Idea Box</h1>
            <p className="font-sans text-sm text-slate-500 mt-2">Share ideas, discover opportunities, upvote what resonates.</p>
          </div>
          <Button
            onClick={() => setShowNew(true)}
            className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans gap-2"
            data-testid="new-idea-btn"
          >
            <Plus className="w-4 h-4" /> New Idea
          </Button>
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2 mb-6">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">Sort:</span>
          <button
            onClick={() => setSortBy("date")}
            className={`px-3 py-1 rounded-full text-xs font-sans transition-colors ${sortBy === "date" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}
            data-testid="sort-date-btn"
          >
            Latest
          </button>
          <button
            onClick={() => setSortBy("upvotes")}
            className={`px-3 py-1 rounded-full text-xs font-sans transition-colors ${sortBy === "upvotes" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}
            data-testid="sort-upvotes-btn"
          >
            Most Upvoted
          </button>
        </div>

        {/* Ideas List */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner"></div></div>
        ) : ideas.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
            <p className="font-sans text-slate-400 mb-2">No ideas yet. Be the first to share!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {ideas.map((idea, i) => (
              <motion.div
                key={idea.idea_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-all"
                data-testid={`idea-card-${idea.idea_id}`}
              >
                <div className="flex gap-4">
                  {/* Upvote */}
                  <button
                    onClick={() => handleUpvote(idea.idea_id)}
                    className={`flex flex-col items-center gap-1 pt-1 transition-colors ${
                      idea.user_upvoted ? "text-blue-600" : "text-slate-300 hover:text-blue-400"
                    }`}
                    data-testid={`upvote-btn-${idea.idea_id}`}
                  >
                    <ThumbsUp className="w-5 h-5" />
                    <span className="text-xs font-mono font-medium">{idea.upvote_count}</span>
                  </button>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-sans text-base font-medium text-slate-900">{idea.title}</h3>
                    {idea.description && (
                      <p className="font-sans text-sm text-slate-500 mt-1 leading-relaxed">{idea.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      <span className="font-sans text-xs text-slate-400">{idea.author_name}</span>
                      <span className="font-mono text-xs text-slate-300">{new Date(idea.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Admin delete */}
                  {user?.role === "admin" && (
                    <button
                      onClick={() => handleDelete(idea.idea_id)}
                      className="text-slate-300 hover:text-red-500 transition-colors self-start"
                      data-testid={`delete-idea-${idea.idea_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* New Idea Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="new-idea-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Share an Idea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Input
                placeholder="Idea title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="font-sans"
                data-testid="idea-title-input"
              />
            </div>
            <div>
              <Textarea
                placeholder="Describe your idea..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="font-sans min-h-[100px]"
                data-testid="idea-description-input"
              />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !title.trim()}
              className="w-full bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans"
              data-testid="submit-idea-btn"
            >
              {submitting ? "Posting..." : "Post Idea"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
